<template lang="pug">
  .log-heart-rate
    .log-heart-rate-content
      .log-heart-rate-header Enter your heart rate.
      .enter-heart-rate-input(:class="{ 'error': showError, 'valid': isValid }")
        .centered-input
          input(
            type="number"
            name="heart"
            v-model="heart"
            placeholder="0"
          )
        .heart-rate-unit bpm
      .error-message(v-if="showError") The value you have entered is not valid. The supported range is 30 to 150 bpm.
      .note-text
        span.note-label Note: 
        | Measure your heart rate in beats per minute right when you wake up or after you’ve been resting.
      .save-button
        WtButton(
          @click.native="saveEntry"
          :loading="savingHeart"
          :disabled="!isValid"
        ) Next
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import analyticsTrackingMixin from "@/mixins/analyticsTrackingMixin";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";

export default {
  name: "LogHeartRate",

  components: {
    WtTitle,
    WtButton,
    WtSvgIcon
  },

  mixins: [analyticsTrackingMixin, dateUtilityMixin],

  props: {
    uniqueKey: {
      type: String,
      default: null
    },

    entryItem: {
      type: Object,
      default: () => ({}),
      required: true
    },

    entryDate: {
      type: String,
      default: ""
    },

    formData: {
      type: FormData,
      required: true,
      validator: value => {
        return value instanceof FormData;
      }
    },

    saveEntryWrapper: {
      type: Function,
      required: true
    },

    disableSave: {
      type: Boolean,
      default: true
    },

    disableReset: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      heart: "",
      savingHeart: false
    };
  },

  computed: {
    currentMetricValue() {
      // Get heart rate from wellnessMetrics following MakeAnEntryHeart pattern
      let metric = this._.find(this.entryItem.wellnessMetrics, {
        type: "heart"
      });
      return this._.get(metric, "value", "");
    },

    setFormData() {
      if (!this._.isEmpty(this.entryItem)) {
        return (
          this.heart !== this.currentMetricValue || this.formData.has("heart")
        );
      }
      return true;
    },

    isValid() {
      return this.heart && this.heart >= 30 && this.heart <= 150;
    },

    showError() {
      return this.heart && !this.isValid;
    }
  },

  watch: {
    heart() {
      if (this.setFormData) {
        this.formData.set("heart", this.heart);
        this.$emit("formDataUpdated", true);
      }
      this.adjustInputWidth();
    },

    entryItem: {
      handler() {
        this.heart = this.currentMetricValue;
        this.adjustInputWidth();
      },
      immediate: true,
      deep: true
    },

    // Watch for uniqueKey changes to reset the form when switching dates
    uniqueKey: {
      handler() {
        this.heart = this.currentMetricValue;
        this.adjustInputWidth();
      },
      immediate: true
    }
  },

  mounted() {
    this.trackFeatureView("tracking", "tracking_heart_rate");
    this.heart = this.currentMetricValue;
    this.adjustInputWidth();
  },

  methods: {
    ...mapActions("members", ["storeJournal", "updateJournal"]),

    adjustInputWidth() {
      this.$nextTick(() => {
        const input = this.$el.querySelector('input[name="heart"]');
        if (input) {
          const span = document.createElement("span");
          span.style.visibility = "hidden";
          span.style.position = "absolute";
          span.style.fontSize = "28px";
          span.style.fontWeight = "500";
          span.style.letterSpacing = "-1%";
          span.textContent = this.heart || "0";
          document.body.appendChild(span);

          const textWidth = span.offsetWidth;
          input.style.width = `${textWidth + 10}px`;

          document.body.removeChild(span);
        }
      });
    },

    saveEntry() {
      if (!this.isValid) return;

      // Always send the value so the backend links the post to this metric
      this.formData.set("heart", this.heart);

      this.savingHeart = true;
      this.saveEntryWrapper(this.storeJournal, this.updateJournal).finally(
        () => {
          this.savingHeart = false;
        }
      );
    },

    reset() {
      const item = "heart";
      if (this.formData.has(item)) {
        this.formData.delete(item);
      }
      this.heart = this.currentMetricValue;
      this.$nextTick(() => {
        this.$emit("formReset", true);
      });
    }
  }
};
</script>

<style lang="sass" scoped>
.log-heart-rate
  height: 100%
  display: flex
  flex-direction: column
  justify-content: space-between

.log-heart-rate-content
  flex: 1
  flex-direction: column
  display: flex
  justify-content: center
  align-items: center

  .log-heart-rate-header
    width: 100%
    text-align: left
    font-weight: 600
    font-size: 20px
    line-height: 28px
    letter-spacing: 0px
    color: #1B1D20
    padding-bottom: 20px

  .enter-heart-rate-input
    width: 100%
    position: relative
    border: 1px solid #E5E7EB
    border-radius: 12px
    background-color: white
    transition: all 0.2s ease
    display: flex
    align-items: center
    justify-content: center
    padding: 10px 12px

    &.valid
      border-color: #3B82F6
      background-color: #EFF6FF

    &.error
      border-color: #EF4444
      background-color: #FEF2F2

    .heart-rate-unit
      font-size: 15px
      line-height: 23px
      letter-spacing: 0px
      font-weight: 500
      color: #9CA3AF
      pointer-events: none
      transform: translateY(20%)

    &.error .heart-rate-unit
      color: #EF4444

    &.error .centered-input ::v-deep input
      color: #EF4444

  .error-message
    width: 100%
    text-align: left
    font-size: 13px
    color: #EC493F
    margin-top: 10px
    line-height: 18px
    font-weight: 500

  .note-text
    width: 100%
    text-align: left
    font-size: 13px
    color: #5D6472
    line-height: 18px
    font-weight: 400
    padding: 20px 0px

    .note-label
      font-weight: 500
      color: #1B1D20

  .save-button
    width: 100%

    ::v-deep .button
      width: 100% !important
      justify-content: center
      font-size: 16px
      line-height: 24px
      letter-spacing: 0px
      background-color: #2972F0
      font-weight: 600

      &:disabled
        background-color: #D1D5DB !important
        border-color: #D1D5DB !important
        color: #9CA3AF !important
        cursor: not-allowed

.centered-input
  display: flex
  align-items: center
  justify-content: center
  flex-shrink: 0

  ::v-deep input
    font-size: 28px
    line-height: 36px
    letter-spacing: -1%
    font-weight: 500
    color: #6B7280
    border: none
    background: transparent
    text-align: center
    padding: 0
    margin: 0
    min-width: 20px

    &:focus
      outline: none

    // Remove number input spinners/arrows
    -webkit-appearance: none
    -moz-appearance: textfield

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button
      -webkit-appearance: none
      margin: 0
      display: none

.log-heart-rate-form-controls
  margin-top: 20px
</style>
